import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Award,
  Users,
  TrendingUp,
  CheckCircle2,
  Send,
  Building,
  Mail,
  Phone,
  Globe,
  FileText,
  Presentation,
  Target,
  Handshake
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import PublicFooter from '../components/PublicFooter';
import SEO, { PageSEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Test sponsor logos for demonstration
const TEST_SPONSOR_LOGOS = [
  { name: 'Texas Home Loans', color: '#1E3A5F' },
  { name: 'Premier Title Services', color: '#0EA5E9' },
  { name: 'Lone Star Mortgage', color: '#10B981' },
  { name: 'Gulf Coast Insurance', color: '#8B5CF6' },
  { name: 'Houston Realty Group', color: '#F59E0B' },
  { name: 'Capital Funding Corp', color: '#EF4444' },
];

function PublicNav() {
  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/public" className="flex items-center gap-2">
            <div className="flex items-center justify-center w-12 h-12 bg-[#1E3A5F] rounded-lg">
              <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4L8 10V18C8 26.84 13.16 34.92 20 37C26.84 34.92 32 26.84 32 18V10L20 4Z" fill="#0EA5E9"/>
                <path d="M16 18L19 21L25 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-[#1E3A5F] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                DealLab
              </span>
              <span className="text-xs text-sky-600 font-medium -mt-0.5">CE Education</span>
            </div>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/public/courses" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Courses
            </Link>
            <Link to="/public/schedule" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Schedule
            </Link>
            <Link to="/public/instructors" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Instructors
            </Link>
            <Link to="/public/sponsors" className="text-[#1E3A5F] font-medium">
              Become a Sponsor
            </Link>
          </div>
          
          <Link to="/public/request">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white">
              Book for Your Team
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

export default function PublicSponsorsPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    description: '',
    why_sponsor: ''
  });

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const response = await axios.get(`${API_URL}/sponsors/approved`);
      setSponsors(response.data);
    } catch (error) {
      console.error('Failed to fetch sponsors');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name.trim() || !formData.contact_name.trim() || !formData.email.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/public/sponsor-application`, formData);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setFormData({
      company_name: '',
      contact_name: '',
      email: '',
      phone: '',
      website: '',
      description: '',
      why_sponsor: ''
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...PageSEO.sponsors} />
      <PublicNav />
      
      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/images/sponsor-classroom.jpeg)'
          }}
        ></div>
        {/* Dark Overlay for text readability */}
        <div className="absolute inset-0 bg-[#1E3A5F]/80"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-sky-500/20 text-sky-300 border-sky-500/30">
              Sponsorship Opportunities
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Partner With Us to Reach Texas Real Estate Professionals
            </h1>
            <p className="text-sky-200 text-lg mb-8">
              Showcase your brand to engaged real estate agents and brokers during 
              our TREC-approved continuing education courses. Get direct exposure to 
              decision-makers in the Texas real estate market.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#apply">
                <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white">
                  Apply to Become a Sponsor
                </Button>
              </a>
              <a href="#benefits">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Learn More
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="bg-white py-12 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-[#1E3A5F]">30+</div>
              <div className="text-slate-500 text-sm">CE Courses</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#1E3A5F]">500+</div>
              <div className="text-slate-500 text-sm">Agents Annually</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#1E3A5F]">56</div>
              <div className="text-slate-500 text-sm">CE Hours Available</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#1E3A5F]">100%</div>
              <div className="text-slate-500 text-sm">TREC Approved</div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Scrolling Sponsor Logo Banner */}
      <section className="py-12 bg-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">
              Our Current Sponsors
            </h3>
            <p className="text-slate-500 text-sm">
              Join these industry leaders in supporting real estate education
            </p>
          </div>
        </div>
        
        <div className="relative">
          {/* Gradient overlays for smooth fade effect */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-slate-50 to-transparent z-10"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-slate-50 to-transparent z-10"></div>
          
          {/* Scrolling container */}
          <div className="flex overflow-hidden">
            <div className="flex animate-scroll gap-20 py-8 items-center">
              {/* First set of logos */}
              {(sponsors.length > 0 ? sponsors : TEST_SPONSOR_LOGOS).map((sponsor, idx) => (
                <div 
                  key={`first-${idx}`}
                  className="flex-shrink-0 flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity duration-300"
                >
                  {sponsor.logo_url ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${sponsor.logo_url}`}
                      alt={sponsor.company_name || sponsor.name}
                      className="h-20 w-auto max-w-[280px] object-contain grayscale hover:grayscale-0 transition-all duration-300"
                    />
                  ) : (
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-14 h-14 rounded-lg flex items-center justify-center bg-slate-300"
                      >
                        <Building className="w-7 h-7 text-slate-500" />
                      </div>
                      <span className="text-slate-500 font-semibold text-xl whitespace-nowrap">
                        {sponsor.company_name || sponsor.name}
                      </span>
                    </div>
                  )}
                </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {(sponsors.length > 0 ? sponsors : TEST_SPONSOR_LOGOS).map((sponsor, idx) => (
                <div 
                  key={`second-${idx}`}
                  className="flex-shrink-0 flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity duration-300"
                >
                  {sponsor.logo_url ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${sponsor.logo_url}`}
                      alt={sponsor.company_name || sponsor.name}
                      className="h-20 w-auto max-w-[280px] object-contain grayscale hover:grayscale-0 transition-all duration-300"
                    />
                  ) : (
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-14 h-14 rounded-lg flex items-center justify-center bg-slate-300"
                      >
                        <Building className="w-7 h-7 text-slate-500" />
                      </div>
                      <span className="text-slate-500 font-semibold text-xl whitespace-nowrap">
                        {sponsor.company_name || sponsor.name}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <style>{`
          @keyframes scroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
          .animate-scroll {
            animation: scroll 25s linear infinite;
          }
          .animate-scroll:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>
      
      {/* Benefits Section */}
      <section id="benefits" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Sponsor Our CE Courses?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Connect with motivated real estate professionals who are actively 
              investing in their careers and seeking quality services.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-slate-200">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-sky-600" />
                </div>
                <h3 className="font-semibold text-[#1E3A5F] text-lg mb-2">Targeted Audience</h3>
                <p className="text-slate-600 text-sm">
                  Reach licensed Texas real estate agents and brokers who are 
                  actively maintaining their credentials and growing their business.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Presentation className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-[#1E3A5F] text-lg mb-2">Premium Visibility</h3>
                <p className="text-slate-600 text-sm">
                  Your logo displayed on dedicated sponsor slides during live 
                  presentations, ensuring maximum attention and brand recall.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Handshake className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="font-semibold text-[#1E3A5F] text-lg mb-2">Build Relationships</h3>
                <p className="text-slate-600 text-sm">
                  Position your brand as a trusted partner in the real estate 
                  industry and build lasting connections with professionals.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              How Sponsorship Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We make it easy for sponsors to get involved and reach our audience.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#1E3A5F] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="font-semibold text-[#1E3A5F] mb-2">Apply</h3>
              <p className="text-slate-600 text-sm">
                Fill out our sponsor application with your company details.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-[#1E3A5F] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="font-semibold text-[#1E3A5F] mb-2">Review</h3>
              <p className="text-slate-600 text-sm">
                Our team reviews your application and reaches out to discuss.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-[#1E3A5F] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="font-semibold text-[#1E3A5F] mb-2">Select Courses</h3>
              <p className="text-slate-600 text-sm">
                Choose the scheduled courses you'd like to sponsor.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-[#1E3A5F] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                4
              </div>
              <h3 className="font-semibold text-[#1E3A5F] mb-2">Get Exposure</h3>
              <p className="text-slate-600 text-sm">
                Your logo appears on dedicated slides during the presentation.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Sponsor Slots Info */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Sponsor Placement
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Each course presentation includes up to 4 dedicated sponsor slides 
              strategically placed throughout the session.
            </p>
          </div>
          
          <Card className="border-slate-200">
            <CardContent className="pt-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
                    <Presentation className="w-5 h-5 text-sky-500" />
                    What's Included
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-slate-600">Full-screen logo display on dedicated sponsor slide</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-slate-600">Your company name announced by the instructor</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-slate-600">Link to your website included on the slide</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-slate-600">10-15 seconds of dedicated screen time</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-sky-500" />
                    Placement Schedule
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <div className="w-6 h-6 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0">1</div>
                      <span className="text-slate-600">Opening - After course introduction</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-6 h-6 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0">2</div>
                      <span className="text-slate-600">Mid-course break point</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-6 h-6 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0">3</div>
                      <span className="text-slate-600">After key content sections</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-6 h-6 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0">4</div>
                      <span className="text-slate-600">Closing - Before Q&A</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      
      {/* Application Form */}
      <section id="apply" className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Apply to Become a Sponsor
            </h2>
            <p className="text-slate-600">
              Fill out the form below and our team will be in touch within 2 business days.
            </p>
          </div>
          
          {submitted ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Application Submitted!
                </h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  Thank you for your interest in sponsoring our CE courses! 
                  Our team will review your application and contact you within 
                  2 business days to discuss sponsorship opportunities.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button 
                    onClick={resetForm}
                    variant="outline" 
                    className="border-[#1E3A5F] text-[#1E3A5F]"
                  >
                    Submit Another Application
                  </Button>
                  <Link to="/public/schedule">
                    <Button className="bg-sky-500 hover:bg-sky-600 text-white">
                      View Course Schedule
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="pt-8 pb-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Company Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
                      <Building className="w-5 h-5 text-sky-500" />
                      Company Information
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label>Company Name *</Label>
                        <Input
                          required
                          value={formData.company_name}
                          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                          placeholder="Your company or business name"
                          data-testid="sponsor-company-name"
                        />
                      </div>
                      
                      <div>
                        <Label>Company Description</Label>
                        <Textarea
                          rows={3}
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="Brief description of your company and services..."
                          data-testid="sponsor-description"
                        />
                      </div>
                      
                      <div>
                        <Label>Company Website</Label>
                        <Input
                          type="url"
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                          placeholder="https://yourcompany.com"
                          data-testid="sponsor-website"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Contact Information */}
                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-sky-500" />
                      Contact Information
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Contact Name *</Label>
                        <Input
                          required
                          value={formData.contact_name}
                          onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                          placeholder="Primary contact person"
                          data-testid="sponsor-contact-name"
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="contact@company.com"
                          data-testid="sponsor-email"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <Label>Phone</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        data-testid="sponsor-phone"
                      />
                    </div>
                  </div>
                  
                  {/* Interest */}
                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-sky-500" />
                      Your Interest
                    </h3>
                    
                    <div>
                      <Label>Why do you want to sponsor our CE courses?</Label>
                      <Textarea
                        rows={4}
                        value={formData.why_sponsor}
                        onChange={(e) => setFormData({ ...formData, why_sponsor: e.target.value })}
                        placeholder="Tell us about your goals and why you'd like to partner with us..."
                        data-testid="sponsor-why"
                      />
                    </div>
                  </div>
                  
                  {/* Submit */}
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full bg-[#1E3A5F] hover:bg-[#162d4a] text-white h-12"
                    data-testid="sponsor-submit"
                  >
                    {submitting ? (
                      <>Submitting...</>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Sponsor Application
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-slate-500 text-center">
                    By submitting this form, you agree to be contacted by our team 
                    regarding sponsorship opportunities.
                  </p>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
      
      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
